// Notifications that a print has started, also while no page is open: Web Push. The browser holds one push
// subscription for this site; every printer the user follows is registered with it on the print server (so one
// browser can follow several printers, and a printer is followed by several browsers). Nothing happens unless the
// user switches a printer's toggle on: the browser's own permission question comes from that click and nowhere else.
// The messages carry no content (the server cannot read prints); the service worker (sw.js) shows the text.

export const SW_URL = '/sw.js';

export class PushError extends Error {
  // reason: 'unsupported' | 'blocked' (the user said no, in the browser) | 'dismissed' (no answer) | 'server'
  constructor(reason, message) {
    super(message);
    this.reason = reason;
  }
}

export const PUSH_MESSAGES = {
  unsupported: 'This browser cannot show notifications while the page is closed (on an iPhone or iPad, add the page to the home screen first).',
  blocked: 'Notifications are blocked for this site: allow them in the browser settings, then switch this on again.',
  dismissed: 'The browser did not get an answer: switch this on again and choose Allow.',
};

export function pushSupported(env = globalThis) {
  return Boolean(env.navigator?.serviceWorker && env.PushManager && env.Notification && env.isSecureContext !== false);
}

export async function endpointHash(endpoint, subtle = globalThis.crypto?.subtle) {
  const digest = new Uint8Array(await subtle.digest('SHA-256', new TextEncoder().encode(endpoint)));
  return [...digest].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function keyBytes(base64url) {
  const text = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(text + '='.repeat((4 - (text.length % 4)) % 4));
  return Uint8Array.from(binary, (c) => c.charCodeAt(0));
}

const sameKey = (buffer, bytes) => {
  const a = new Uint8Array(buffer ?? []);
  return a.length === bytes.length && a.every((v, i) => v === bytes[i]);
};

async function registration(env, create) {
  const sw = env.navigator.serviceWorker;
  if (create) {
    await sw.register(SW_URL);
    return sw.ready;
  }
  return (await sw.getRegistration(SW_URL)) ?? null;
}

// Switches notifications on for one printer: asks the browser's permission (call this from the click), makes the
// subscription if there is none, tells the print server. client: a ReaderClient of that printer.
export async function enableNotifications({ client, env = globalThis }) {
  if (!pushSupported(env)) throw new PushError('unsupported', PUSH_MESSAGES.unsupported);
  let permission = env.Notification.permission;
  if (permission === 'default') permission = await env.Notification.requestPermission();
  if (permission === 'denied') throw new PushError('blocked', PUSH_MESSAGES.blocked);
  if (permission !== 'granted') throw new PushError('dismissed', PUSH_MESSAGES.dismissed);
  return subscribeAndRegister({ client, env });
}

// Permission is there: the subscription (made again if the browser lost it or the server's key changed) and the
// registration of this printer with the server.
async function subscribeAndRegister({ client, env }) {
  const reg = await registration(env, true);
  const serverKey = keyBytes(await client.pushKey());
  let sub = await reg.pushManager.getSubscription();
  if (sub && !sameKey(sub.options?.applicationServerKey, serverKey)) {
    await sub.unsubscribe(); // made for another server key: the print server could not use it
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: serverKey });
  try {
    await client.subscribePush(sub.toJSON());
  } catch (e) {
    throw new PushError('server', `The print server did not accept the subscription: ${e.message}`);
  }
  return sub;
}

// Switches it off for one printer. othersFollow: this browser still follows other printers (the subscription stays).
export async function disableNotifications({ client, othersFollow, env = globalThis }) {
  if (!pushSupported(env)) return;
  const reg = await registration(env, false);
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (!sub) return;
  try {
    await client.unsubscribePush(await endpointHash(sub.endpoint));
  } catch (e) {
    if (e.status !== 404 && e.status !== 401) throw e; // already gone, or the server forgot the printer: nothing to undo
  }
  if (!othersFollow) await sub.unsubscribe();
}

// At startup, silently: for the printers the user follows, make sure the server still has this browser's
// subscription (it may have been renewed or forgotten). Never asks anything. Without permission (or support) the
// toggles can no longer work: those entries are returned, so the page can show them as off. A server that does not
// answer is not a reason to switch anything off: the next start tries again.
// followed: [{client, ...whatever the caller wants back}].
export async function syncNotifications({ followed, env = globalThis }) {
  if (followed.length === 0) return [];
  if (!pushSupported(env) || env.Notification.permission !== 'granted') return followed;
  for (const entry of followed) {
    try {
      await subscribeAndRegister({ client: entry.client, env });
    } catch {
      // offline, or the server is busy: keep the toggle, try again at the next start
    }
  }
  return [];
}
